import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Stethoscope, ShieldCheck, HandHeart, MapPin, PawPrint, ChevronRight, X, ChevronLeft } from "lucide-react";
import BrandLogo from "./BrandLogo";

const KEY = "petbond_onboarded_v1";

export default function Onboarding({ onSignIn }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [persona, setPersona] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(KEY);
    if (!seen) {
      const t = setTimeout(() => setVisible(true), 350); // tiny pause for splash feel
      return () => clearTimeout(t);
    }
  }, []);

  const close = (signin = false) => {
    localStorage.setItem(KEY, JSON.stringify({ at: new Date().toISOString(), persona }));
    setVisible(false);
    if (signin && onSignIn) onSignIn();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-bg" data-testid="onboarding-overlay">
      <div className="absolute inset-0 bg-gradient-to-br from-brand/10 via-bg to-leaf/30"/>
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-brand/20 rounded-full blur-3xl animate-pulse"/>
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-brand-blue/20 rounded-full blur-3xl animate-pulse"/>
      <button onClick={() => close(false)} data-testid="onboarding-skip" className="absolute top-4 right-4 text-ink-soft text-sm font-bold hover:text-ink z-10 inline-flex items-center gap-1">
        Skip <X className="w-3 h-3"/>
      </button>
      <div className="relative h-full flex items-center justify-center p-4">
        <div className="w-full max-w-xl">
          <div key={step} className="animate-[slideup_500ms_cubic-bezier(0.16,1,0.3,1)]">
            {step === 0 && <SlideWelcome onNext={() => setStep(1)}/>}
            {step === 1 && <SlideFeatures onNext={() => setStep(2)} onBack={() => setStep(0)}/>}
            {step === 2 && <SlidePersona persona={persona} setPersona={setPersona} onNext={() => setStep(3)} onBack={() => setStep(1)}/>}
            {step === 3 && <SlideFinal persona={persona} onSignIn={() => close(true)} onBack={() => setStep(2)}/>}
          </div>
          <div className="mt-8 flex items-center justify-center gap-2" data-testid="onboarding-progress">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-10 bg-brand" : i < step ? "w-6 bg-brand/40" : "w-6 bg-surface-muted"}`}/>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fadein { from { opacity:0; transform: translateY(12px); } to { opacity:1; transform:none; } }
        @keyframes slideup { from { opacity:0; transform: translateY(24px) scale(0.98); } to { opacity:1; transform: none; } }
        @keyframes floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .floaty { animation: floaty 4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function NavButtons({ onBack, onNext, nextLabel, nextDisabled, nextTestId }) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      {onBack ? (
        <button onClick={onBack} className="text-ink-soft text-sm font-bold inline-flex items-center gap-1 hover:text-ink"><ChevronLeft className="w-4 h-4"/> Back</button>
      ) : <span/>}
      <button data-testid={nextTestId || "onboarding-next"} disabled={nextDisabled} onClick={onNext} className={`rounded-full px-6 py-3 text-sm font-bold inline-flex items-center gap-1.5 transition-all ${nextDisabled ? "bg-surface-muted text-ink-soft" : "bg-ink text-white hover:bg-brand hover:-translate-y-0.5"}`}>
        {nextLabel || "Continue"} <ChevronRight className="w-4 h-4"/>
      </button>
    </div>
  );
}

function SlideWelcome({ onNext }) {
  return (
    <div className="text-center" data-testid="slide-welcome">
      <div className="floaty inline-block">
        <BrandLogo variant="full" size="lg"/>
      </div>
      <div className="mt-8 text-[11px] uppercase tracking-[0.3em] text-brand font-bold">India's #1 Pet Care Super-App</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold mt-3 leading-tight">
        A healthier,<br/><span className="text-brand italic font-medium">happier</span> life for your pet.
      </h1>
      <p className="text-ink-soft mt-4 text-base max-w-md mx-auto">
        Vets in 60 seconds. AI symptom check 24/7. Foster, adopt or sponsor a rescue. One app, every life-stage.
      </p>
      <NavButtons onNext={onNext} nextLabel="Show me how" nextTestId="onboard-start"/>
    </div>
  );
}

function SlideFeatures({ onNext, onBack }) {
  const items = [
    { icon: Sparkles, title: "Dr. PetBond AI", line: "24/7 symptom checker powered by Claude", bg: "bg-brand text-white", img: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&q=80" },
    { icon: Stethoscope, title: "Verified vets", line: "Book real-time slots in 60s", bg: "bg-brand-blue text-white", img: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&q=80" },
    { icon: ShieldCheck, title: "QR Pet ID", line: "Print posters · Lost pets reunited", bg: "bg-brand-green text-white", img: "https://images.unsplash.com/photo-1601758124510-52d02ddb7cbd?w=400&q=80" },
    { icon: HandHeart, title: "Rescue-to-Home", line: "NGOs, fosters & sponsors united", bg: "bg-sun text-ink", img: "https://images.unsplash.com/photo-1517849845537-4d257902454a?w=400&q=80" },
  ];
  return (
    <div data-testid="slide-features">
      <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-center leading-tight">Everything pets, in one place</h2>
      <p className="text-ink-soft text-center text-sm mt-2">Tap to explore. We'll personalise next.</p>
      <div className="grid grid-cols-2 gap-3 mt-6">
        {items.map((it, i) => (
          <div key={i} className="bg-surface border border-line rounded-2xl p-4 overflow-hidden relative shadow-soft hover:-translate-y-1 transition-all" style={{ animationDelay: `${i * 80}ms` }}>
            <div className={`w-9 h-9 rounded-xl grid place-items-center ${it.bg}`}><it.icon className="w-5 h-5"/></div>
            <div className="font-heading font-extrabold mt-2.5 text-sm">{it.title}</div>
            <div className="text-xs text-ink-soft mt-0.5">{it.line}</div>
            <img src={it.img} alt="" className="absolute -bottom-4 -right-4 w-16 h-16 rounded-full object-cover opacity-30"/>
          </div>
        ))}
      </div>
      <div className="mt-6 bg-leaf/40 border border-leaf rounded-2xl p-4 flex items-center gap-3">
        <div className="text-3xl">🐾</div>
        <div className="text-sm">
          <div className="font-bold">Built with verified NGOs · vets · stores</div>
          <div className="text-xs text-ink-soft">100% of NGO donations go straight to the NGO · no platform fee</div>
        </div>
      </div>
      <NavButtons onBack={onBack} onNext={onNext}/>
    </div>
  );
}

function SlidePersona({ persona, setPersona, onNext, onBack }) {
  const options = [
    { id: "parent", label: "I have a pet", desc: "Vets, AI, food, meetups, hostels", icon: PawPrint, accent: "border-brand bg-brand/5" },
    { id: "adopt",  label: "I want to adopt", desc: "Browse adoption + foster pets nearby", icon: HandHeart, accent: "border-brand-green bg-brand-green/5" },
    { id: "lost",   label: "I lost my pet",   desc: "Free poster, alert nearby NGOs in 60s", icon: MapPin, accent: "border-brand-blue bg-brand-blue/5" },
    { id: "partner", label: "I run a clinic / NGO", desc: "Vet, store, hostel or NGO portal", icon: Stethoscope, accent: "border-sun bg-sun/30" },
  ];
  return (
    <div data-testid="slide-persona">
      <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-center leading-tight">What brings you to PetBond?</h2>
      <p className="text-ink-soft text-center text-sm mt-2">We'll set up your app accordingly.</p>
      <div className="space-y-2 mt-6">
        {options.map(o => {
          const selected = persona === o.id;
          return (
            <button key={o.id} data-testid={`persona-${o.id}`} onClick={() => setPersona(o.id)} className={`w-full text-left rounded-2xl p-4 border-2 transition-all flex items-center gap-3 ${selected ? o.accent : "border-line bg-surface hover:border-ink-soft"}`}>
              <div className={`w-10 h-10 rounded-xl grid place-items-center ${selected ? "bg-ink text-white" : "bg-surface-muted text-ink-soft"}`}><o.icon className="w-5 h-5"/></div>
              <div className="flex-1">
                <div className="font-heading font-extrabold">{o.label}</div>
                <div className="text-xs text-ink-soft mt-0.5">{o.desc}</div>
              </div>
              {selected && <div className="w-5 h-5 rounded-full bg-ink text-white text-xs grid place-items-center font-bold">✓</div>}
            </button>
          );
        })}
      </div>
      <NavButtons onBack={onBack} onNext={onNext} nextDisabled={!persona} nextLabel="Continue"/>
    </div>
  );
}

function SlideFinal({ persona, onSignIn, onBack }) {
  const headline = {
    parent: "Your pet's new best friend",
    adopt: "Your next family member awaits",
    lost: "Let's bring them home",
    partner: "Welcome to your command center",
  }[persona] || "You're all set";
  return (
    <div className="text-center" data-testid="slide-final">
      <div className="text-6xl floaty">🐾</div>
      <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mt-4 leading-tight">{headline}</h2>
      <p className="text-ink-soft mt-3 max-w-md mx-auto">
        Sign in with Google to save your pet's records, get AI vet help, and unlock a 7-day free Premium trial. No card required.
      </p>
      <div className="mt-6 inline-flex items-center gap-2 bg-leaf/40 rounded-full px-4 py-1.5 text-xs font-bold text-ink">
        <ShieldCheck className="w-3.5 h-3.5"/> Your data stays private. Always.
      </div>
      <div className="mt-6">
        <button data-testid="onboard-signin-cta" onClick={onSignIn} className="bg-ink text-white rounded-full px-8 py-4 text-base font-bold inline-flex items-center gap-2 hover:bg-brand transition-colors hover:-translate-y-0.5">
          Continue with Google
        </button>
      </div>
      <NavButtons onBack={onBack} onNext={onSignIn} nextLabel="Get started →" nextTestId="onboard-final-next"/>
    </div>
  );
}
